import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, MapPin, Briefcase } from "lucide-react";
import { photoUrl, useAccount } from "@/lib/account";
import { usePrefs } from "@/lib/prefs";
import { compatibility } from "@/lib/compat";
import ShortlistButton from "@/components/ShortlistButton";

export const AiBadge = ({ testId = "ai-profile-badge" }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gradient-to-r from-purple-900/85 to-indigo-900/85 text-purple-200 border border-purple-500/30 backdrop-blur">
    <Sparkles className="w-3 h-3" /> AI Profile
  </span>
);

export const MemberBadge = ({ testId = "member-profile-badge" }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-950/85 text-emerald-300 border border-emerald-500/30 backdrop-blur">
    <ShieldCheck className="w-3 h-3" /> Member
  </span>
);

export default function ProfileCard({ p, index = 0 }) {
  const { profile: me } = useAccount();
  const { t } = usePrefs();
  const score = compatibility(me, p);
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}>
      <Link to={`/profile/${p.id}`} data-testid={`profile-card-${p.id}`} className="card-royal group block rounded-2xl overflow-hidden">
        <div className="relative aspect-[4/5] overflow-hidden bg-[#0D0F15]">
          {p.photos?.[0] ? (
            <img src={photoUrl(p.photos[0])} alt={p.name} loading="lazy" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-6xl text-amber-500/40">{p.name?.[0]}</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          <div className="absolute top-3 left-3">{p.is_ai ? <AiBadge testId={`ai-badge-${p.id}`} /> : <MemberBadge testId={`member-badge-${p.id}`} />}</div>
          <ShortlistButton pid={p.id} className="absolute top-3 right-3" />
          {score && <span data-testid={`compat-badge-${p.id}`} className="absolute top-14 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-600/85 text-white backdrop-blur">{score}% {t("match")}</span>}
          <div className="absolute bottom-0 p-5 w-full">
            <h3 className="font-display text-2xl font-semibold text-white">{p.name}, <span className="text-amber-300">{p.age}</span></h3>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-300">
              <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{p.profession}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.city}{p.country && p.country !== "India" ? `, ${p.country}` : p.country ? ", India" : ""}</span>
            </div>
            <p className="mt-1 text-xs text-slate-400">{p.religion} · {p.mother_tongue}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
